/**
 * Users API module.
 *
 * Provides operations for cleaners (aseadoras), clients (clientes),
 * owners (propietarios), and general user management (update, delete,
 * avatar upload).
 *
 * Key change: `createClient` and `createOwner` were nearly identical —
 * both now delegate to `createContact(rol, userData)` to avoid duplication.
 */

import { apiFetch, type OpcionesLlamada } from "./apiClient";
import {
  ApiResponse,
  RegisterRequest,
  UpdateUserRequest,
  User,
  Cleaner,
  Client,
  CleanerResponse,
  ClientResponse,
} from "../interfaces/api.interfaces";

/**
 * Trabajador (rol sin login) — round 2026-04-30. Empleado de Aloja Fácil
 * que no es aseador, cliente ni propietario (mantención, supervisor,
 * contador externo, etc.). Sólo registro para rastrear pagos.
 */
export interface Trabajador {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  rut: string;
  banco: string;
  tipocuenta: string;
  numerocuenta: string;
  descripcion: string;
}

/**
 * Lo que se manda al crear o editar un trabajador. `telefono`: `null` vacía el teléfono, un texto lo reemplaza y OMITIRLO
 * (undefined) lo deja como está (ronda F2: el mismo contrato en todos los formularios de teléfono).
 */
export type TrabajadorCreate = Omit<Trabajador, "id" | "telefono"> & { telefono?: string | null };

export const usersApi = {
  // ─── Cleaners (aseadoras) ─────────────────────────────────────────────────

  /** Returns all aseadoras registered in the system. */
  getCleaners: async (): Promise<Cleaner[] | null> => {
    const data = await apiFetch<CleanerResponse>("/users/cleaners", {
      errorMsg: "Error al obtener información de las aseadoras",
    });
    if (!data?.success) return null;
    return data.data;
  },

  /** Returns a single aseadora by ID. */
  getCleanerById: async (id: number): Promise<Cleaner | null> => {
    const data = await apiFetch<ApiResponse<Cleaner>>(`/users/cleaners/${id}`, {
      errorMsg: "Error al obtener información de la aseadora",
    });
    return data?.data ?? null;
  },

  // ─── General user operations ──────────────────────────────────────────────

  /**
   * Updates user fields for any role. `sinAvisoDeExito`: no muestra «Usuario actualizado correctamente» (quien llama decide el
   * aviso después de mirar la respuesta, p. ej. Mi perfil cuando el servidor no aplicó lo único que se pidió).
   */
  updateUser: (
    id: number,
    userData: UpdateUserRequest,
    opciones: OpcionesLlamada & { sinAvisoDeExito?: boolean } = {},
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
      successMsg: opciones.sinAvisoDeExito ? undefined : "Usuario actualizado correctamente",
      errorMsg: "Error al actualizar la información del usuario",
      alFallar: opciones.alFallar,
    }),

  /** PATCH /users/:id/password — change own password. Requires the current
   * password to avoid account takeover in case of stolen JWT. */
  changePassword: (
    id: number,
    currentPassword: string,
    newPassword: string,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/users/${id}/password`, {
      method: "PATCH",
      body: JSON.stringify({ currentPassword, newPassword }),
      successMsg: "Contraseña actualizada correctamente",
      errorMsg: "Error al cambiar la contraseña",
    }),

  /** POST /users/:id/send-recovery — un admin fuerza el envío del correo de
   * recuperación de contraseña de un usuario. `email` opcional sobreescribe la
   * dirección de destino (no cambia el email de la cuenta). */
  sendRecovery: (
    id: number,
    email?: string,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/users/${id}/send-recovery`, {
      method: "POST",
      body: JSON.stringify(email ? { email } : {}),
      errorMsg: "Error al enviar el correo de recuperación",
    }),

  /** GET /users/:id/check-delete — pre-check before deletion. */
  checkDelete: (id: number): Promise<any | null> =>
    apiFetch<any>(`/users/${id}/check-delete`, {
      errorMsg: "Error al verificar usuario",
    }),

  /** Permanently deletes a user by ID. */
  deleteUser: async (id: number): Promise<ApiResponse<any> | null> => {
    const ok = await apiFetch<any>(`/users/${id}`, {
      method: "DELETE",
      successMsg: "Usuario eliminado correctamente",
      errorMsg: "Error al eliminar el usuario",
    });
    return ok !== null ? { success: true } : null;
  },

  /** Returns the full profile of a user by ID. */
  getUser: async (id: number): Promise<User | null> => {
    const data = await apiFetch<ApiResponse<User>>(`/users/${id}`, {
      errorMsg: "Error al obtener información del usuario",
    });
    return data?.data ?? null;
  },

  // ─── Clients & owners ────────────────────────────────────────────────────

  /**
   * Creates a contact user (cliente or propietario).
   * Consolidates the previously duplicated `createClient` and `createOwner`
   * functions which differed only in the `rol` value.
   */
  createContact: (
    rol: "cliente" | "propietario",
    userData: RegisterRequest,
  ): Promise<ApiResponse<any> | null> => {
    // Post-refactor: "propietario" se unificó a "cliente". El backend
    // normaliza igual, pero enviamos rol escalar directo.
    const rolFinal = "cliente";
    const payload = {
      nombre_usuario: userData.nombre,
      email_usuario: userData.email,
      telefono_usuario: userData.telefono || "",
      rol: rolFinal,
    };
    const label = rol === "cliente" ? "Cliente" : "Propietario";
    return apiFetch<ApiResponse<any>>("/auth/register/client", {
      method: "POST",
      body: JSON.stringify(payload),
      successMsg: `${label} registrado correctamente`,
      errorMsg: `Error al registrar al ${label.toLowerCase()}`,
    });
  },

  /** Returns all users with role `cliente`. */
  getClients: async (): Promise<Client[] | null> => {
    const data = await apiFetch<ClientResponse>("/users/clients", {
      errorMsg: "Error al obtener información de los clientes",
    });
    if (!data?.success) return null;
    return data.data;
  },

  /** Returns all users with role `propietario`. */
  getOwners: async (): Promise<Client[] | null> => {
    const data = await apiFetch<ClientResponse>("/users/owners", {
      errorMsg: "Error al obtener información de los propietarios",
    });
    if (!data?.success) return null;
    return data.data;
  },

  /** Updates client-specific fields (propiedades_maneja, etc.). */
  updateClient: (
    id: number,
    userData: UpdateUserRequest,
  ): Promise<ApiResponse<any> | null> =>
    apiFetch<ApiResponse<any>>(`/users/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
      successMsg: "Usuario actualizado correctamente",
      errorMsg: "Error al actualizar la información del usuario",
    }),

  // ─── Trabajadores (rol sin login) ────────────────────────────────────
  // Round 2026-04-30 — empleados de Aloja Fácil que no son aseadores ni
  // propietarios. Permiten rastrear pagos asociados sin darles acceso
  // al sistema. Manejados desde la página /trabajadores.

  /** GET /api/users/trabajadores — lista de trabajadores. */
  getTrabajadores: async (): Promise<Trabajador[] | null> => {
    const data = await apiFetch<{ success: boolean; data: Trabajador[] }>(
      "/users/trabajadores",
      { errorMsg: "Error al obtener trabajadores" },
    );
    if (!data?.success) return null;
    return data.data;
  },

  /** POST /api/users/trabajadores — crea un trabajador. */
  createTrabajador: async (
    payload: TrabajadorCreate,
    opciones: OpcionesLlamada = {},
  ): Promise<Trabajador | null> => {
    const data = await apiFetch<{ success: boolean; data: Trabajador }>(
      "/users/trabajadores",
      {
        method: "POST",
        body: JSON.stringify(payload),
        successMsg: "Trabajador creado",
        errorMsg: "Error al crear trabajador",
        alFallar: opciones.alFallar,
      },
    );
    return data?.data ?? null;
  },

  /**
   * PUT /api/users/trabajadores/:id — actualiza datos de un trabajador. `sinAvisoDeExito`: no muestra «Trabajador actualizado»
   * (quien llama decide el aviso después de mirar la respuesta, p. ej. cuando el servidor no aplicó lo único que se pidió: vaciar
   * el teléfono). Mismo contrato que `updateUser`.
   */
  updateTrabajador: async (
    id: number,
    payload: TrabajadorCreate,
    opciones: OpcionesLlamada & { sinAvisoDeExito?: boolean } = {},
  ): Promise<Trabajador | null> => {
    const data = await apiFetch<{ success: boolean; data: Trabajador }>(
      `/users/trabajadores/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
        successMsg: opciones.sinAvisoDeExito ? undefined : "Trabajador actualizado",
        errorMsg: "Error al actualizar trabajador",
        alFallar: opciones.alFallar,
      },
    );
    return data?.data ?? null;
  },

  // ─── Avatar management ────────────────────────────────────────────────────

  /** Uploads an image file as the user's avatar. Returns the new avatar URL. */
  uploadUserAvatar: async (id: number, file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("avatar", file);
    const data = await apiFetch<any>(`/users/profile/avatar/${id}`, {
      method: "POST",
      body: formData,
      successMsg: "Avatar actualizado correctamente",
      errorMsg: "Error al subir avatar",
    });
    return data?.avatar_url ?? null;
  },

  /** Saves a Multiavatar seed string as the user's avatar. Returns the seed. */
  updateUserAvatarSeed: async (
    id: number,
    seed: string,
  ): Promise<string | null> => {
    const data = await apiFetch<any>(`/users/profile/avatar/${id}`, {
      method: "POST",
      body: JSON.stringify({ avatarSeed: seed }),
      successMsg: "Avatar actualizado correctamente",
      errorMsg: "Error al actualizar avatar",
    });
    return (data?.avatar_seed as string) ?? null;
  },
};
